<template>
  <v-dialog
    v-model="show"
    persistent
    @keydown.esc="hide"
    width="500"
    v-if="role('admin', 'safetyRepresentative')"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        v-if="role('admin', 'safetyRepresentative')"
        v-on="on"
        :loading="$apollo.loading"
        class="primary accent-4 white--text mr-2"
      >
        <v-icon dark left>add</v-icon>
        {{ $t("instructions.group.change") }}
      </v-btn>
    </template>

    <v-card :loading="$apollo.loading || loading">
      <v-card-title class="headline grey lighten-2" primary-title>
        {{ $t("instructions.group.change") }}
      </v-card-title>
      <v-card-text>
        <div class="pt-3 pb-1">{{ $t("instructions.group.explanation") }}</div>
        <v-chip-group
          v-model="selection"
          @change="handleGroupSelectionChange"
          column
          active-class="primary--text"
        >
          <v-chip
            v-for="(group, index) in groups"
            :key="index"
            class="mr-1 mb-1"
            small
            :close="canDelete(group.name)"
            close-icon="delete_forever"
            @click:close="deleteGroup(group)"
            >{{ group.name }}</v-chip
          >
        </v-chip-group>
        <div v-if="newGroup && similar.length" class="my-2">
          {{ $t(`instructions.group.similar`) }}:
          {{ similar.join(", ") }}
        </div>

        <!-- @submit.prevent prevents submit on enter press -->
        <v-form @submit.prevent ref="form">
          <v-text-field
            v-model="newGroup"
            :label="$t('instructions.group.new')"
            hide-details="auto"
            :rules="[rules.required, rules.exists]"
          />
        </v-form>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-spacer></v-spacer>

        <v-btn color="grey" text @click="hide">{{ $t("dialog.cancel") }}</v-btn>

        <v-btn color="primary" text @click="add" :loading="loading">{{
          $t("dialog.add")
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

import InstructionGroupsQuery from "@/graphql/queries/InstructionGroups.gql";
import InstructionsQuery from "@/graphql/queries/instruction/Instructions.gql";

import CreateInstructionGroupName from "@/graphql/mutation/instruction/CreateInstructionGroupName.gql";
import DeleteInstructionGroup from "@/graphql/mutation/instruction/DeleteInstructionGroup.gql";
import ChangeInstructionGroupOrder from "@/graphql/mutation/instruction/ChangeInstructionGroupOrder.gql";

export default {
  data() {
    return {
      loading: false,
      show: false,
      newGroup: "",
      rules: {},
      selection: null,
      previousSelection: null,
    };
  },
  mounted() {
    this.deactivateRules();
  },
  computed: {
    ...mapGetters(["role"]),
    similar() {
      return (
        (this.groups &&
          this.groups
            .map((e) => e.name)
            .filter((n) =>
              n.toLowerCase().includes(this.newGroup.toLowerCase())
            )) ||
        []
      );
    },
    groupsUsedInInstructions() {
      return (
        (this.instructions &&
          this.instructions
            .map((instruction) => instruction.groups.map((g) => g.name))
            .flat()
            .filter((v, i, a) => a.indexOf(v) === i)) ||
        []
      );
    },
  },
  methods: {
    handleGroupSelectionChange(selection) {
      if (selection == this.previousSelection) {
        this.selection = null;
        return;
      }
      if (this.previousSelection != null) {
        this.changeInstructionGroupOrder(
          this.previousSelection,
          this.selection
        );

        return;
      }
      this.previousSelection = selection;
    },
    canDelete(group) {
      return (
        this.groups && !this.groupsUsedInInstructions.some((x) => x === group)
      );
    },
    hide() {
      this.deactivateRules();
      this.$refs.form.resetValidation();
      this.newGroup = "";
      this.show = false;
    },
    add() {
      this.activateRules();
      this.$nextTick(() => {
        if (this.$refs.form.validate()) {
          this.loading = true;
          this.$apollo
            .mutate({
              mutation: CreateInstructionGroupName,
              variables: {
                name: this.newGroup,
              },
            })
            .then(() => {
              this.$store.commit("push_global_message", {
                color: "success",
                text: `${this.$t("instructions.group.new")} ${this.$t(
                  "instructions.group.added",
                  {
                    text: this.newGroup,
                  }
                )}`,
              });
            })
            .catch((err) => {
              this.$store.commit("push_global_message", {
                color: "error",
                text: err,
              });
            })
            .finally(() => {
              this.hide();
              this.loading = false;
            });
        }
      });
    },
    deleteGroup(group) {
      if (!group) {
        return;
      }
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: DeleteInstructionGroup,
          variables: {
            id: group.id,
          },
        })
        .then(() => {
          this.$apollo.queries.groups.refresh();
          this.$store.commit("push_global_message", {
            color: "warning",
            text: `${this.$t("instructions.group.deleted")} ${group.name}`,
          });
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            color: "error",
            text: err,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    deactivateRules() {
      this.rules = {
        required: () => true,
        exists: () => true,
      };
    },
    activateRules() {
      this.rules = {
        required: () => !!this.newGroup || this.$i18n.t("validation.required"),
        exists: () =>
          !this.groups
            .map((e) => e.name.toLowerCase())
            .includes(this.newGroup.toLowerCase()) ||
          this.$i18n.t("validation.categoryExists"),
      };
    },
    changeInstructionGroupOrder(oldOrderIndex, newOrderIndex) {
      if (
        !Number.isInteger(oldOrderIndex) ||
        !Number.isInteger(newOrderIndex)
      ) {
        return;
      }
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: ChangeInstructionGroupOrder,
          variables: {
            oldOrderIndex,
            newOrderIndex,
          },
        })
        .then((res) => {
          this.groups = res.data.groups;
          this.$store.commit("push_global_message", {
            color: "warning",
            text: `order changed`,
          });
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            color: "error",
            text: err,
          });
        })
        .finally(() => {
          this.loading = false;
          this.selection = null;
          this.previousSelection = null;
        });
    },
  },
  apollo: {
    instructions: {
      query: InstructionsQuery,
      skip() {
        return !this.show;
      },
    },
    groups: {
      query: InstructionGroupsQuery,
      skip() {
        return !this.show;
      },
    },
  },
};
</script>
